/* FTE Report (including New Hire Plan) — current FTE mix (Employee/IWO/Subcontractor) per month
   plus the planned new-hire forecast, ported from "FTE Report including New Hire Plan - New
   report request.xlsx". Two tabs (Summary, Details); page chrome, filter bar and download icon
   are the same system AOP Budget uses — this file is structurally a near-copy of AOPBudget.js.
   Page styles and the filter bar are split into their own files (see ./fte-report/) to keep this
   file to state/behavior + layout.

   Data comes from /reports/fte_new_hire_plan/* (see APIIntegrationFiles/FTE_NEW_HIRE_PLAN_API.md);
   Summary.js/Details.js own their fetch + loading state, this file owns the Excel download. */
import React, { useCallback, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useReportFonts } from "../reportFonts";
import BackButton from "../BackButton";
import { goToReportsCatalog } from "../reportNav";
import {
  FILTERS_CONFIG,
  initFilterValues,
  isFilterActive,
  getDisplayValue,
  getDefaultValue,
  getActiveFilterCount,
  areVisibleFilterValuesEqual,
} from "./GlobalFilterPanel";
import { useExpandedAppliedFilters } from "./useExpandedAppliedFilters";
import { applyCascadeDraftChange, pruneInvalidCascadeSelections } from "./filterRelationshipUtils";
import { usePlByProgramStyles, PlPageHeader } from "./pl-by-program/plByProgramTheme";
import ActualsVsForecastByDateRangePicker from "./pl-by-program/ActualsVsForecastByDateRangePicker";
import { defaultDateRange } from "./pl-by-program/actualsVsForecastByDateRangeUtils";
import { useFteReportStyles } from "./fte-report/fteReportPageStyles";
import { filtersForKeys, isDefaultFilters } from "./fte-report/fteReportFilterHelpers";
import { futureDateRange } from "./fte-report/fteReportUtils";
import FTEReportFilterBar from "./fte-report/FTEReportFilterBar";
import Summary from "./fte-report/Summary";
import Details from "./fte-report/Details";
import Loading from "../../../common-components/Loading";
import ActionMessages from "../../../common-components/ActionMessages";
import { downloadFteSummaryAPI, downloadFteDetailsAPI } from "../../../api/reports-api";

const TABS = [
  { key: "summary", label: "Summary" },
  { key: "details", label: "Details" },
];

const FTE_FILTER_KEYS = [
  "contractId",
  "bu",
  "projectId",
  "clinLvl2",
  "popEndDate",
  "programManager",
  "pc",
  "contractType",
];

const DOWNLOAD_BY_TAB = {
  summary: { api: downloadFteSummaryAPI, filename: "fte-report-summary.xlsx" },
  details: { api: downloadFteDetailsAPI, filename: "fte-report-details.xlsx" },
};

const TAB_FILTERS = {
  summary: FTE_FILTER_KEYS,
  details: FTE_FILTER_KEYS,
};

function FilterIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

export default function FTEReport() {
  const classes = useFteReportStyles();
  const plClasses = usePlByProgramStyles();
  useReportFonts();
  const history = useHistory();

  const { tab: tabParam } = useParams();
  const activeTab = TABS.find((t) => t.key === tabParam)?.key ?? TABS[0].key;
  const [filterOpen, setFilterOpen] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState(initFilterValues);
  const [draftFilters, setDraftFilters] = useState(initFilterValues);
  // Each tab keeps its own range: Summary opens on the trailing 12 months, Details (future hires
  // only) on the next 12 months.
  const [ranges, setRanges] = useState(() => ({ summary: defaultDateRange(), details: futureDateRange() }));
  const dateRange = ranges[activeTab];
  const setDateRange = useCallback((r) => setRanges((prev) => ({ ...prev, [activeTab]: r })), [activeTab]);
  const [downloadMsg, setDownloadMsg] = useState("");
  const [downloading, setDownloading] = useState(false);

  const visibleKeys = TAB_FILTERS[activeTab] || null;
  const { dropdownOptions, relationshipRows, effectiveFilters } = useExpandedAppliedFilters(
    appliedFilters,
    draftFilters,
    visibleKeys,
  );

  const filterCount = getActiveFilterCount(appliedFilters, visibleKeys);

  const activeChips = filtersForKeys(visibleKeys).filter((f) => isFilterActive(f, appliedFilters[f.key]));

  const clearChip = useCallback((f) => {
    setAppliedFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
    setDraftFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
  }, []);

  const clearAllApplied = useCallback(() => {
    const reset = (prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    };
    setAppliedFilters(reset);
    setDraftFilters(reset);
  }, [visibleKeys]);

  const handleDraftChange = useCallback(
    (key, value) => {
      setDraftFilters((prev) => applyCascadeDraftChange(prev, key, value, relationshipRows, visibleKeys));
    },
    [relationshipRows, visibleKeys],
  );

  const handleApply = useCallback(() => {
    const pruned = pruneInvalidCascadeSelections(draftFilters, relationshipRows, visibleKeys);
    const next = { ...initFilterValues() };
    (visibleKeys || []).forEach((k) => {
      next[k] = pruned[k];
    });
    setAppliedFilters(next);
  }, [draftFilters, relationshipRows, visibleKeys]);

  const clearAll = useCallback(() => {
    setDraftFilters((prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
  }, [visibleKeys]);

  const TAB_HEADER = {
    summary: { eyebrow: "FTE Report", title: "Summary", accent: "Current FTE & New Hire Plan" },
    details: { eyebrow: "FTE Report", title: "Details", accent: "Forecasted New Hire Report" },
  };

  const handleDownload = useCallback(async () => {
    const entry = DOWNLOAD_BY_TAB[activeTab];
    if (!entry || downloading) return;
    setDownloading(true);
    try {
      const { blob, filename } = await entry.api(effectiveFilters, dateRange);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || entry.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("FTE Report download failed:", err);
      setDownloadMsg("Download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  }, [activeTab, effectiveFilters, dateRange, downloading]);

  return (
    <div className={classes.page}>
      {downloading && <Loading />}
      {downloadMsg && (
        <ActionMessages type="info" message={downloadMsg} isClosed={() => setDownloadMsg("")} />
      )}
      {/* ── Top bar ── */}
      <div className={classes.topBar}>
        <BackButton onClick={() => goToReportsCatalog(history)} ariaLabel="Back to Reports" />
        <nav className={classes.crumb}>
          <span className={classes.crumbLink} onClick={() => goToReportsCatalog(history)}>
            Reports
          </span>
          <span className={classes.crumbSep}>&gt;</span>
          <span className={classes.crumbCur}>FTE Report</span>
        </nav>
        <div className={classes.topActions}>
          <button
            type="button"
            className={`${classes.actionBtn} ${classes.actionBtnIconOnly} ${classes.actionBtnActive}`}
            title="Download as Excel"
            aria-label="Download Report"
            onClick={handleDownload}
            disabled={downloading}
          >
            <DownloadIcon />
          </button>
          <button
            type="button"
            className={`${classes.actionBtn} ${filterOpen || filterCount > 0 ? classes.actionBtnActive : ""}`}
            onClick={() => setFilterOpen((p) => !p)}
          >
            <FilterIcon />
            Filters
            {filterCount > 0 && <span className={classes.filterBadge}>{filterCount}</span>}
          </button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className={classes.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${classes.tab} ${activeTab === tab.key ? classes.tabActive : ""}`}
            onClick={() => history.push(`/projects/reports/fte_report/${tab.key}`)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Horizontal filter bar ── */}
      {filterOpen && (
        <FTEReportFilterBar
          visibleKeys={visibleKeys}
          draft={draftFilters}
          dropdownOptions={dropdownOptions}
          onChange={handleDraftChange}
          onClearAll={clearAll}
          onApply={handleApply}
          applyDisabled={areVisibleFilterValuesEqual(draftFilters, appliedFilters, visibleKeys)}
          onClose={() => setFilterOpen(false)}
          classes={classes}
          clearAllDisabled={isDefaultFilters(draftFilters, visibleKeys)}
        />
      )}

      {/* ── Active filter chips ── */}
      {!filterOpen && activeChips.length > 0 && (
        <div className={classes.chipsBar}>
          {activeChips.map((f) => {
            const val = appliedFilters[f.key];
            const tip =
              f.type === "multiselect" && Array.isArray(val) && val.length > 1
                ? `${f.label}: ${val.join(", ")}`
                : `${f.label}: ${getDisplayValue(f, val)}`;
            return (
              <span key={f.key} className={classes.chip} title={tip}>
                <span className={classes.chipLabel}>{f.label}:</span>
                {getDisplayValue(f, val)}
                <button className={classes.chipClear} onClick={() => clearChip(f)} title={`Clear ${f.label}`}>
                  ✕
                </button>
              </span>
            );
          })}
          <button className={classes.chipsClearAll} onClick={clearAllApplied} disabled={isDefaultFilters(appliedFilters, visibleKeys)}>
            Clear All
          </button>
        </div>
      )}

      {/* ── Page header ── */}
      <div className={classes.headerContainer}>
        <PlPageHeader
          classes={plClasses}
          eyebrow={TAB_HEADER[activeTab].eyebrow}
          title={TAB_HEADER[activeTab].title}
          accent={TAB_HEADER[activeTab].accent}
          badge={TAB_HEADER[activeTab].badge}
        />
      </div>

      {/* ── Date Range — same position "Actuals Vs Forecast by Date Range" renders its own
          picker in: right after the page header, above the tab content. ── */}
      <div className={classes.headerContainer}>
        <ActualsVsForecastByDateRangePicker
          startMonth={dateRange.startMonth}
          startYear={dateRange.startYear}
          endMonth={dateRange.endMonth}
          endYear={dateRange.endYear}
          onChange={setDateRange}
        />
      </div>

      {/* ── Content ── */}
      <div className={classes.body}>
        <div className={classes.content}>
          {activeTab === "summary" && <Summary filters={effectiveFilters} dateRange={dateRange} />}
          {activeTab === "details" && <Details filters={effectiveFilters} dateRange={dateRange} />}
        </div>
      </div>
    </div>
  );
}
